import {
  doc,
  setDoc,
  getDoc,
  onSnapshot,
  updateDoc,
  Unsubscribe,
  collection,
  query,
  where,
  limit,
  getDocs,
} from 'firebase/firestore';
import { getFirebaseDb } from './config';
import { GameMode, GameState, BotProfile } from '../engine/types';

export interface RoomPlayer {
  id: string;
  name: string;
  isBot: boolean;
  botProfile?: BotProfile;
  team?: 'A' | 'B';
  score: number;
  connected: boolean;
}

export interface ChatMessage {
  id: string;
  sender: string;
  text: string;
  timestamp: number;
}

export interface RoomSession {
  id: string; // 4 to 12 characters (e.g. "SLIME-8")
  hostId: string;
  mode: GameMode;
  isPublic: boolean;
  status: 'waiting' | 'playing' | 'finished';
  players: RoomPlayer[];
  gameState?: GameState;
  chat: ChatMessage[];
  updatedAt: number;
}

export function generateRoomCode(): string {
  const words = ['SLIME', 'BIT', 'BLUFF', 'GOLD', 'EZAR', 'KORAM'];
  const word = words[Math.floor(Math.random() * words.length)];
  const num = Math.floor(10 + Math.random() * 89);
  return `${word}-${num}`.toUpperCase(); // Length e.g. "SLIME-82" (8 chars, 4-12 range)
}

export function withTimeout<T>(promise: Promise<T>, timeoutMs: number = 2200): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) =>
      setTimeout(() => reject(new Error('Firebase operation timed out')), timeoutMs)
    ),
  ]);
}

export async function createRoom(
  roomId: string,
  hostId: string,
  hostName: string,
  mode: GameMode,
  isPublic: boolean = false
): Promise<RoomSession> {
  const db = getFirebaseDb();
  if (!db) {
    throw new Error('Firebase Firestore is not initialized or offline.');
  }

  const normalizedId = roomId.trim().toUpperCase();
  if (normalizedId.length < 4 || normalizedId.length > 12) {
    throw new Error('Room ID must be between 4 and 12 characters.');
  }

  const session: RoomSession = {
    id: normalizedId,
    hostId,
    mode,
    isPublic,
    status: 'waiting',
    players: [
      {
        id: hostId,
        name: hostName,
        isBot: false,
        score: 0,
        connected: true,
      },
    ],
    chat: [],
    updatedAt: Date.now(),
  };

  const sessionRef = doc(db, 'sessions', normalizedId);
  await withTimeout(setDoc(sessionRef, session), 2200);
  return session;
}

export async function getRoom(roomId: string): Promise<RoomSession | null> {
  const db = getFirebaseDb();
  if (!db) return null;

  try {
    const sessionRef = doc(db, 'sessions', roomId.trim().toUpperCase());
    const snap = await withTimeout(getDoc(sessionRef), 2200);
    if (snap.exists()) {
      return snap.data() as RoomSession;
    }
  } catch (err) {
    console.warn('[Firestore] getRoom timed out or failed:', err);
  }
  return null;
}

export async function joinRoom(
  roomId: string,
  playerId: string,
  playerName: string
): Promise<RoomSession> {
  const db = getFirebaseDb();
  if (!db) throw new Error('Firestore not available');

  const normalizedId = roomId.trim().toUpperCase();
  const sessionRef = doc(db, 'sessions', normalizedId);
  const snap = await withTimeout(getDoc(sessionRef), 2200);

  if (!snap.exists()) {
    throw new Error('ROOM_NOT_FOUND');
  }

  const room = snap.data() as RoomSession;
  const maxPlayers = room.mode === 'duel' ? 2 : 4;

  const existingPlayerIndex = room.players.findIndex((p) => p.id === playerId);
  if (existingPlayerIndex !== -1) {
    // Reconnection
    room.players[existingPlayerIndex].connected = true;
    withTimeout(
      updateDoc(sessionRef, {
        players: room.players,
        updatedAt: Date.now(),
      }),
      2200
    ).catch((err) => console.warn('Update reconnect error:', err));
    return room;
  }

  if (room.players.length >= maxPlayers) {
    throw new Error('ROOM_FULL');
  }

  const newPlayer: RoomPlayer = {
    id: playerId,
    name: playerName,
    isBot: false,
    score: 0,
    connected: true,
  };

  const updatedPlayers = [...room.players, newPlayer];
  withTimeout(
    updateDoc(sessionRef, {
      players: updatedPlayers,
      updatedAt: Date.now(),
    }),
    2200
  ).catch((err) => console.warn('Update join error:', err));

  return { ...room, players: updatedPlayers };
}

export async function updateRoomState(
  roomId: string,
  updates: Partial<RoomSession>
): Promise<void> {
  const db = getFirebaseDb();
  if (!db) return;

  try {
    const sessionRef = doc(db, 'sessions', roomId.trim().toUpperCase());
    await withTimeout(
      updateDoc(sessionRef, {
        ...updates,
        updatedAt: Date.now(),
      }),
      2000
    );
  } catch (err) {
    console.warn('[Firestore] updateRoomState non-fatal error or timeout:', err);
  }
}

export function subscribeToRoom(
  roomId: string,
  onUpdate: (session: RoomSession) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  const db = getFirebaseDb();
  if (!db) {
    return () => {};
  }

  const sessionRef = doc(db, 'sessions', roomId.trim().toUpperCase());
  return onSnapshot(
    sessionRef,
    (snap) => {
      if (snap.exists()) {
        onUpdate(snap.data() as RoomSession);
      }
    },
    (err) => {
      console.warn('[Firestore] Room subscription error:', err);
      if (onError) onError(err);
    }
  );
}

export async function fetchPublicRooms(): Promise<RoomSession[]> {
  const db = getFirebaseDb();
  if (!db) return [];

  try {
    const q = query(
      collection(db, 'sessions'),
      where('isPublic', '==', true),
      where('status', '==', 'waiting'),
      limit(10)
    );
    const snap = await withTimeout(getDocs(q), 2200);
    const rooms: RoomSession[] = [];
    snap.forEach((docSnap) => {
      rooms.push(docSnap.data() as RoomSession);
    });
    return rooms;
  } catch (err) {
    console.warn('[Firestore] fetchPublicRooms error:', err);
    return [];
  }
}
